import { getLandingPageContent, getWorkoutLanding } from "./api";
import {
  getInternalWorkoutLanding,
  getLandingPageContentInternal,
} from "./internalRequest";
import {
  getE2ELandingPageContent,
  getE2EWorkoutLanding,
  isE2EMockApi,
} from "./e2eMockData";
import { getCFCountry, getPaymentCountry } from "./setCookie";
import {
  noteSsrCacheBypass,
  ssrCacheConfig,
  ssrCachedFetch,
  stripPriceAndPromoFields,
} from "./ssrCache";

const isServerRequest = (ctx) => !!(ctx && ctx.req);

const getDirectHeaders = (params = {}) => {
  const country = params.country || params.country_code;
  return country ? { "x-client-country-code": String(country).toUpperCase() } : {};
};

const getResponseData = (response, fallback = null) => (
  response && response.data ? response.data : fallback
);

// ---------------------------------------------------------------------------
// SSR response cache (server-only, anonymous traffic only).
//
// Landing getInitialProps menunggu 2 call Laravel berurutan; tiap call ~1-3s
// sehingga TTFB root document pernah ~7s dan membunuh FCP/LCP/PageSpeed. Konten
// landing + pricing adalah data CMS yang identik untuk semua pengunjung dengan
// (page_source, language, country, flag promo) yang sama — aman di-cache singkat.
//
// - Hanya untuk SSR (ctx.req ada). Request browser tidak lewat sini.
// - Request dengan authorization header TIDAK di-cache (bisa user-specific).
// - Hanya response non-empty yang disimpan — kegagalan upstream tidak di-cache.
// - TTL 15m (jitter, tidak pernah lebih panjang) + stale-while-revalidate 1h:
//   setelah hit pertama, semua request anonim mendapat data instan; refresh
//   CMS/pricing tetap merambat <= 15m.
// - In-memory per proses PM2 — cukup untuk memotong TTFB tanpa infra baru.
//
// Trello 430 WP-13 (D-29 A) — the engine (helpers/ssrCache.js) adds single-flight
// per key, a bounded stale-on-error copy, no refresh loop against a throttling
// backend and LRU eviction. THIS file decides what a key is:
//
//  * every input that changes the backend answer is in the key, including the
//    VISITOR COUNTRY (the x-client-country-code / x-http-ipcountry headers the
//    request really carries, resolved exactly like helpers/axios.js does) —
//    before, the content call and any pricing call without `country` shared one
//    entry across countries;
//  * every client-controlled input is validated and canonicalised (case of the
//    country, empty/undefined, key order); a value that does not look valid is
//    NOT cached at all (the request goes to the backend exactly as before), so a
//    scanner cycling `?c=<random>` can neither bloat nor poison the cache;
//  * ids chosen by the client (promo id, collab slug/l) live in a small separate
//    budget so they cannot evict the hot base entries.
//
// Prices and promo are never served from an expired copy by the scopes WP-13
// added (proxy-pricing, collab-pricing): fresh TTL <= 15 min, no
// stale-while-revalidate window, no stale-on-error; once the TTL ends the next
// request asks the backend (single-flight still collapses concurrent loads).
// The pre-existing SSR landing-pricing scope keeps exactly its pre-WP-13
// windows (15 min fresh + 60 min stale-while-revalidate, no stale-on-error), so
// a price there is never older than before this card. The content scope (which
// carries the promo percentage) may be served expired only on backend trouble
// (no HTTP status, 408, 429, >= 500), with price/promo members stripped.
// ---------------------------------------------------------------------------
const INVALID = { invalid: true };

const isAbsent = (value) => value === undefined || value === null || value === "";

const tokenDim = (pattern) => (value) => {
  if (isAbsent(value)) return undefined;
  if (typeof value !== "string" && typeof value !== "number") return INVALID;
  const text = String(value);
  return pattern.test(text) ? text : INVALID;
};

const boolDim = (value) => {
  if (isAbsent(value)) return undefined;
  if (value === true || value === "true" || value === 1 || value === "1") return "t";
  if (value === false || value === "false" || value === 0 || value === "0") return "f";
  return INVALID;
};

// ISO-3166 alpha-2 or the site's "worldwide" marker, upper-cased.
const countryDim = (value) => {
  if (isAbsent(value)) return undefined;
  if (typeof value !== "string") return INVALID;
  const upper = value.trim().toUpperCase();
  return upper === "WORLDWIDE" || /^[A-Z]{2}$/.test(upper) ? upper : INVALID;
};

const PRICING_DIMS = {
  language: tokenDim(/^[A-Za-z]{2}(-[A-Za-z]{2})?$/),
  // "/deals" may also arrive percent-encoded ("%2Fdeals"); kept verbatim so the
  // key matches exactly what the backend is asked.
  page_source: tokenDim(/^(?:\/|%2[Ff])?[A-Za-z0-9][A-Za-z0-9_/-]{0,39}$/),
  promo_id: tokenDim(/^[A-Za-z0-9_-]{1,64}$/),
  promo_price: boolDim,
  reseller_price: boolDim,
  retargeting_price: boolDim,
  promo_tr_price: boolDim,
  context: tokenDim(/^[A-Za-z0-9_-]{1,24}$/),
  country: countryDim,
  country_code: countryDim,
};

const COLLAB_DIMS = {
  ...PRICING_DIMS,
  slug: tokenDim(/^[A-Za-z0-9_.-]{1,96}$/),
  l: tokenDim(/^[A-Za-z0-9_-]{1,64}$/),
};

const CONTENT_DIMS = {
  page_source: PRICING_DIMS.page_source,
};

const VARIANT_PARAMS = ["promo_id", "slug", "l"];

// page_source values the site itself uses. /plan takes page_source from the
// query string, so any other (valid-looking) value is client-chosen: it is
// cached, but in the small variant budget.
const KNOWN_PAGE_SOURCE = /^(?:\/|%2[Ff])?(premium|plan|plan_layout|promo|flashsale|flashdeals|limited-offer|deals|special|collab|quiz|questionnaire|diskon|xt|cms|lite_only)(\/[A-Za-z0-9_-]{1,24})?$/;

/**
 * Canonical cache key, or null when the request must not be cached (an unknown
 * or invalid-looking input, or an unusable country). `geo` is the visitor
 * country part from geoFromCtx()/geoFromHeaders().
 */
const buildSsrKey = (scope, dims, params, geo) => {
  if (geo === null || geo === undefined) return null;
  const source = params || {};
  const parts = [];
  const names = Object.keys(source).sort();
  for (let i = 0; i < names.length; i += 1) {
    const name = names[i];
    const value = source[name];
    if (!isAbsent(value)) {
      // Own keys only: `name` is a client-chosen query parameter, and a name that
      // exists on Object.prototype (hasOwnProperty, valueOf, __lookupGetter__, ...)
      // must be an unknown input, not an inherited function.
      const normalize = Object.prototype.hasOwnProperty.call(dims, name) ? dims[name] : undefined;
      if (!normalize) return null; // an input we do not know the meaning of
      const canonical = normalize(value);
      if (canonical === INVALID) return null;
      if (canonical !== undefined) parts.push(`${name}=${canonical}`);
    }
  }
  const variant = VARIANT_PARAMS.some((name) => !isAbsent(source[name]))
    || (!isAbsent(source.page_source) && !KNOWN_PAGE_SOURCE.test(String(source.page_source)));
  return { key: `${scope}|${parts.join("&")}|geo=${geo}`, variant };
};

const geoPart = (clientCountry, edgeCountry) => {
  const client = countryDim(isAbsent(clientCountry) ? undefined : String(clientCountry));
  const edge = countryDim(isAbsent(edgeCountry) ? undefined : String(edgeCountry));
  if (client === INVALID || edge === INVALID) return null;
  return `${client || "-"}/${edge || "-"}`;
};

// The two headers helpers/axios.js puts on the backend call for an SSR render:
// x-client-country-code = params.country (upper-cased) else the payment country,
// x-http-ipcountry = the Cloudflare country. Same functions, same ctx.
const geoFromCtx = (ctx, params) => {
  const explicit = params && (params.country || params.country_code);
  const client = explicit ? String(explicit) : getPaymentCountry(ctx);
  return geoPart(client, getCFCountry(ctx));
};

// Next API proxies: the same two headers, read from the incoming request.
const headerValue = (headers, name) => {
  const value = headers && headers[name];
  return Array.isArray(value) ? value[0] : value;
};
// A browser that has no country cookie yet sends neither header and the backend
// is then free to look at the connecting IP, so the real edge country
// (cf-ipcountry, set by Cloudflare on the incoming request) is part of the key
// too. An unusable value (Tor "T1", garbage) means: do not cache.
const geoFromHeaders = (headers) => {
  const base = geoPart(
    headerValue(headers, "x-client-country-code"),
    headerValue(headers, "x-http-ipcountry")
  );
  const rawEdge = headerValue(headers, "cf-ipcountry");
  const edge = countryDim(isAbsent(rawEdge) ? undefined : String(rawEdge));
  if (base === null || edge === INVALID) return null;
  return `${base}/${edge || "-"}`;
};

// Scope policies. CONTENT = CMS landing content (carries the promo percentage);
// PRICING = packages/prices/promo.
const CONTENT_POLICY = {
  staleOnError: true,
  scrub: stripPriceAndPromoFields,
};
// Pre-existing SSR landing-pricing scope ONLY (zero-difference with the code
// before WP-13: 15 min fresh + 60 min stale-while-revalidate, errors propagate).
// SSR_CACHE_PRICING_STALE_MS may shorten that stale window; 0 makes it strictly
// TTL-bound. It does NOT touch the scopes below.
const pricingPolicy = () => {
  const policy = { staleOnError: false };
  const raw = process.env.SSR_CACHE_PRICING_STALE_MS;
  if (raw !== undefined && raw !== "" && isFinite(Number(raw)) && Number(raw) >= 0) {
    policy.swrMs = Number(raw);
  }
  return policy;
};
// Scopes WP-13 newly put behind the cache (proxy pricing, collab pricing): a
// price is never older than the SSR landing page already shows today, so the
// fresh TTL is capped at that scope's 15 min, there is no stale window and no
// stale-on-error. No env knob can re-enable stale serving here.
const NEW_PRICING_MAX_TTL_MS = 15 * 60 * 1000;
const newPricingPolicy = () => ({
  staleOnError: false,
  swrMs: 0,
  ttlMs: Math.min(NEW_PRICING_MAX_TTL_MS, ssrCacheConfig().ttlMs),
});

/** Kill switch for the WP-13 coverage that did not exist before (static pages, collab, proxies). */
export const isExtendedSsrCacheEnabled = () => process.env.SSR_CACHE_EXTENDED !== "0";

const cachedOrDirect = (scope, dims, params, geo, policy, loader, forceVariant) => {
  const built = buildSsrKey(scope, dims, params, geo);
  if (!built) {
    noteSsrCacheBypass();
    return loader();
  }
  return ssrCachedFetch(
    { ...policy, key: built.key, variant: built.variant || !!forceVariant },
    loader
  );
};

/**
 * Landing loaders run in the browser during client navigation and on the Next
 * server for the initial render. Browser calls keep using the same-origin Next
 * proxy; SSR calls Laravel directly because FULL_DOMAIN self-fetch fails on
 * prod.mfsdev.net.
 */
export const fetchLandingPageContent = async (ctx, params = {}) => {
  if (isServerRequest(ctx) && !isE2EMockApi()) {
    const load = () => getLandingPageContent(params, getDirectHeaders(params), ctx)
      .then((response) => getResponseData(response, {}));
    return cachedOrDirect(
      "landing-content",
      CONTENT_DIMS,
      params,
      geoFromCtx(ctx, params),
      CONTENT_POLICY,
      load
    );
  }

  const response = isServerRequest(ctx)
    ? { data: getE2ELandingPageContent() }
    : await getLandingPageContentInternal(params, {}, ctx);

  return getResponseData(response, {});
};

export const fetchLandingPricing = async (
  ctx,
  params = {},
  proxyHeaders = {}
) => {
  // Jangan cache request ber-auth — respons bisa membawa state user.
  const isAnonymous = !(proxyHeaders && proxyHeaders.authorization);
  const useCache = isServerRequest(ctx) && !isE2EMockApi() && isAnonymous;

  const request = (requestParams) => {
    if (isServerRequest(ctx) && isE2EMockApi()) {
      return Promise.resolve({ data: getE2EWorkoutLanding() });
    }
    return isServerRequest(ctx)
      ? getWorkoutLanding(requestParams, getDirectHeaders(requestParams), ctx)
      : getInternalWorkoutLanding(requestParams, proxyHeaders, ctx);
  };

  const fetchFresh = async () => {
    const response = await request(params);
    const content = getResponseData(response);
    const normalizedPageSource = decodeURIComponent(params.page_source || "");

    // Keep the /deals contract identical to pages/api/workout-landing: its cards
    // come from promo when the dedicated response has no selling-price group.
    if (
      normalizedPageSource === "/deals"
      && content
      && (!content.selling_price_plan || content.selling_price_plan.length === 0)
    ) {
      const promoResponse = await request({ ...params, page_source: "promo" });
      const promoContent = getResponseData(promoResponse);
      if (promoContent) {
        content.selling_price_plan = (
          promoContent.selling_price_plan && promoContent.selling_price_plan.length
        )
          ? promoContent.selling_price_plan
          : (promoContent.plan || []);
      }
    }
    return content;
  };

  if (!useCache) {
    return fetchFresh();
  }
  return cachedOrDirect(
    "landing-pricing",
    PRICING_DIMS,
    params,
    geoFromCtx(ctx, params),
    pricingPolicy(),
    fetchFresh
  );
};

/**
 * Collab get-plan / package-list SSR (helpers/collabLandingSsr.js). Only the
 * anonymous render is shared: a request that carries a session token asks the
 * backend as that user and is never cached. `slug` / `l` are part of the key.
 */
export const fetchCollabPricingCached = (ctx, params, loader) => {
  const eligible = isExtendedSsrCacheEnabled() && isServerRequest(ctx) && !isE2EMockApi();
  if (!eligible) return loader();
  return cachedOrDirect(
    "collab-pricing",
    COLLAB_DIMS,
    params,
    geoFromCtx(ctx, params),
    newPricingPolicy(),
    loader
  );
};

/**
 * Next API proxies for the landing data (pages/api/workout-landing,
 * pages/api/workout-landing/landing-page-data). Browser navigations used to go
 * straight to the backend on every call, each one counted against the same
 * prod-fe IP. Anonymous calls are now shared; a call with an Authorization
 * header never is.
 *
 * @param {"pricing"|"content"} kind
 * @param {Object} params   query parameters the proxy forwards to the backend
 * @param {Object} headers  the incoming request's headers
 * @param {Function} loader () => Promise<envelope>  (whole backend envelope)
 */
export const cachedLandingProxy = (kind, params, headers, loader) => {
  const authenticated = !!(headers && headers.authorization);
  if (!isExtendedSsrCacheEnabled() || authenticated) return loader();
  const isPricing = kind === "pricing";
  return cachedOrDirect(
    isPricing ? "proxy-pricing" : "proxy-content",
    isPricing ? PRICING_DIMS : CONTENT_DIMS,
    params,
    geoFromHeaders(headers),
    isPricing ? newPricingPolicy() : CONTENT_POLICY,
    loader
  );
};

// CMS static content (legal pages, /page/*) through pages/api/static/[id].js.
// The answer depends on the slug and the language only, there is no price in
// it, so an expired copy may be served when the backend is failing. CMS edits
// stay visible quickly (5 min fresh + 10 min stale-while-revalidate, shorter
// than the 15 + 60 of the landing data). Slugs are chosen by the client, so
// every entry lives in the small variant budget.
const STATIC_SLUG = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,95}$/;
const STATIC_DIMS = { language: PRICING_DIMS.language };
const STATIC_POLICY = {
  staleOnError: true,
  ttlMs: 5 * 60 * 1000,
  swrMs: 10 * 60 * 1000,
};

export const cachedStaticProxy = (slug, params, loader) => {
  if (!isExtendedSsrCacheEnabled()) return loader();
  if (typeof slug !== "string" || !STATIC_SLUG.test(slug)) {
    noteSsrCacheBypass();
    return loader();
  }
  return cachedOrDirect(`proxy-static:${slug}`, STATIC_DIMS, params, "-", STATIC_POLICY, loader, true);
};

// Exposed for the tests.
export const __internals = {
  buildSsrKey,
  geoFromCtx,
  geoFromHeaders,
  PRICING_DIMS,
  COLLAB_DIMS,
  CONTENT_DIMS,
};
